import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_URL, SITE_NAME, DEFAULT_IMAGE } from '../../utils/seoConstants';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  canonicalPage?: number;
  twitter?: {
    card?: string;
    site?: string;
    title?: string;
    description?: string;
    image?: {
      url?: string;
      alt?: string;
      width?: number;
      height?: number;
    };
  };
  og?: {
    title?: string;
    description?: string;
    url?: string;
    type?: string;
    siteName?: string;
    locale?: string;
    image?: {
      url?: string;
      alt?: string;
      width?: number;
      height?: number;
      type?: string;
      secureUrl?: string;
    };
    profile?: {
      firstName?: string;
      lastName?: string;
      username?: string;
      gender?: string;
    };
    article?: {
      publishedTime?: string;
      modifiedTime?: string;
      expirationTime?: string;
      authors?: string[];
      section?: string;
      tags?: string[];
    };
  };
  geo?: {
    region?: string;
    position?: string;
    placename?: string;
    icbm?: string;
  };
  language?: string;
  authorMeta?: string;
  additionalMetaTags?: Array<{ [key: string]: string }>;
  additionalLinkTags?: Array<{ [key: string]: string }>;
  languageAlternates?: Array<{ hrefLang: string; href: string }>;
}

const MAX_DESCRIPTION_LENGTH = 160;

const truncateDescription = (desc: string, maxLength: number = MAX_DESCRIPTION_LENGTH): string => {
  if (desc.length <= maxLength) return desc;
  return desc.substring(0, maxLength - 3).trim() + '...';
};

const normalizePath = (path: string): string => {
  if (path !== '/' && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
};

const getFullUrl = (url: string | undefined, defaultPath: string, canonicalPage?: number): string => {
  // If url is a full URL (starts with http), use it directly
  if (url?.startsWith('http')) {
    return url;
  }
  
  // If url is provided and starts with /, prepend SITE_URL
  if (url?.startsWith('/')) {
    const pathWithPage = canonicalPage ? `${url}?page=${canonicalPage}` : url;
    return `${SITE_URL}${pathWithPage}`;
  }
  
  // Otherwise use the default path with SITE_URL
  const pathWithPage = canonicalPage ? `${defaultPath}?page=${canonicalPage}` : defaultPath;
  return `${SITE_URL}${pathWithPage}`;
};

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  noIndex = false,
  noFollow = false,
  canonicalPage,
  twitter,
  og,
  geo,
  language,
  authorMeta,
  additionalMetaTags,
  additionalLinkTags,
  languageAlternates,
}: SEOProps) => {
  const location = useLocation();
  const normalizedPath = normalizePath(location.pathname);
  const fullUrl = getFullUrl(url, normalizedPath, canonicalPage);
  
  const defaultTitle = `${SITE_NAME} - Find Best Online Tutors`;
  const defaultDescription = 'Connect with expert tutors worldwide. Get personalized one-on-one tutoring in math, science & languages. Book free trial today!';
  
  const pageTitle = title || defaultTitle;
  const metaDescription = truncateDescription(description || defaultDescription);
  const ogImage = image || DEFAULT_IMAGE;

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  useEffect(() => {
    const head = document.head;
    
    const getOrCreate = (tag: string, attrs: Record<string, string>): HTMLElement => {
      let element = document.querySelector(tag) as HTMLElement;
      if (!element) {
        element = document.createElement(tag.split('[')[0]);
        head.appendChild(element);
      }
      Object.entries(attrs).forEach(([key, value]) => {
        element.setAttribute(key, value);
      });
      return element;
    };

    const getOrCreateMeta = (name: string, content: string, isProperty = false): HTMLMetaElement => {
      const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        head.appendChild(meta);
      }
      meta.setAttribute('content', content);
      return meta;
    };

    const getOrCreateLink = (rel: string, href: string, extraAttrs?: Record<string, string>): HTMLLinkElement => {
      const selector = `link[rel="${rel}"]`;
      let link = document.querySelector(selector) as HTMLLinkElement;
      
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        head.appendChild(link);
      }
      link.href = href;
      if (extraAttrs) {
        Object.entries(extraAttrs).forEach(([key, value]) => {
          link.setAttribute(key, value);
        });
      }
      return link;
    };

    const getOrCreateCanonical = (href: string): HTMLLinkElement => {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        head.appendChild(canonical);
      }
      canonical.href = href;
      return canonical;
    };

    // Basic Meta Tags
    getOrCreateMeta('description', metaDescription);
    
    if (keywords) {
      getOrCreateMeta('keywords', keywords);
    }

    if (language) {
      getOrCreateMeta('language', language);
    }

    if (authorMeta || author) {
      getOrCreateMeta('author', authorMeta || author || '');
    }

    // Robots
    const robotsContent = noIndex ? 'noindex, nofollow' : (noFollow ? 'index, nofollow' : 'index, follow');
    getOrCreateMeta('robots', robotsContent);

    // Canonical
    getOrCreateCanonical(fullUrl);

    // Geo Tags
    if (geo) {
      if (geo.region) getOrCreateMeta('geo.region', geo.region);
      if (geo.position) getOrCreateMeta('geo.position', geo.position);
      if (geo.placename) getOrCreateMeta('geo.placename', geo.placename);
      if (geo.icbm) getOrCreateMeta('ICBM', geo.icbm);
    }

    // Open Graph
    const ogTitle = og?.title || pageTitle;
    const ogDescription = og?.description || metaDescription;
    const ogType = og?.type || type;
    const ogSiteName = og?.siteName || SITE_NAME;
    const ogUrl = og?.url 
      ? (og.url.startsWith('http') ? og.url : `${SITE_URL}${og.url}`)
      : fullUrl;
    
    getOrCreateMeta('og:title', ogTitle, true);
    getOrCreateMeta('og:description', ogDescription, true);
    getOrCreateMeta('og:url', ogUrl, true);
    getOrCreateMeta('og:type', ogType.toLowerCase(), true);
    getOrCreateMeta('og:site_name', ogSiteName, true);
    
    const finalOgImage = og?.image?.url || ogImage;
    const ogImageUrl = finalOgImage.startsWith('http') ? finalOgImage : `${SITE_URL}${finalOgImage}`;
    getOrCreateMeta('og:image', ogImageUrl, true);
    
    if (og?.image?.alt) {
      getOrCreateMeta('og:image:alt', og.image.alt, true);
    }
    if (og?.image?.width) {
      getOrCreateMeta('og:image:width', og.image.width.toString(), true);
    }
    if (og?.image?.height) {
      getOrCreateMeta('og:image:height', og.image.height.toString(), true);
    }
    if (og?.image?.type) {
      getOrCreateMeta('og:image:type', og.image.type, true);
    }
    if (og?.locale) {
      getOrCreateMeta('og:locale', og.locale, true);
    }

    // OG Type specific tags
    if (og?.type?.toLowerCase() === 'profile' && og.profile) {
      if (og.profile.firstName) getOrCreateMeta('profile:first_name', og.profile.firstName, true);
      if (og.profile.lastName) getOrCreateMeta('profile:last_name', og.profile.lastName, true);
      if (og.profile.username) getOrCreateMeta('profile:username', og.profile.username, true);
      if (og.profile.gender) getOrCreateMeta('profile:gender', og.profile.gender, true);
    }

    if (og?.type?.toLowerCase() === 'article' && og.article) {
      if (og.article.publishedTime) getOrCreateMeta('article:published_time', og.article.publishedTime, true);
      if (og.article.modifiedTime) getOrCreateMeta('article:modified_time', og.article.modifiedTime, true);
      if (og.article.expirationTime) getOrCreateMeta('article:expiration_time', og.article.expirationTime, true);
      if (og.article.authors?.length) {
        og.article.authors.forEach(a => getOrCreateMeta('article:author', a, true));
      }
      if (og.article.section) getOrCreateMeta('article:section', og.article.section, true);
      if (og.article.tags?.length) {
        og.article.tags.forEach(tag => getOrCreateMeta('article:tag', tag, true));
      }
    }

    // Fallback for article/author
    if (publishedTime) {
      getOrCreateMeta('article:published_time', publishedTime, true);
    }
    if (modifiedTime) {
      getOrCreateMeta('article:modified_time', modifiedTime, true);
    }
    if (author) {
      getOrCreateMeta('article:author', author, true);
    }

    // Twitter Card
    const twitterCard = twitter?.card || 'summary_large_image';
    getOrCreateMeta('twitter:card', twitterCard);
    
    if (twitter?.site) {
      getOrCreateMeta('twitter:site', twitter.site);
    }
    getOrCreateMeta('twitter:title', twitter?.title || pageTitle);
    getOrCreateMeta('twitter:description', twitter?.description || metaDescription);
    getOrCreateMeta('twitter:url', fullUrl);
    
    const twitterImage = twitter?.image?.url || ogImage;
    const twitterImageUrl = twitterImage.startsWith('http') ? twitterImage : `${SITE_URL}${twitterImage}`;
    getOrCreateMeta('twitter:image', twitterImageUrl);
    if (twitter?.image?.alt) {
      getOrCreateMeta('twitter:image:alt', twitter.image.alt);
    }

    // Language Alternates
    if (languageAlternates?.length) {
      languageAlternates.forEach(langAlt => {
        getOrCreateLink('alternate', langAlt.href, { hrefLang: langAlt.hrefLang });
      });
    }

    // Additional Meta Tags
    if (additionalMetaTags?.length) {
      additionalMetaTags.forEach(tag => {
        const el = document.createElement('meta');
        Object.entries(tag).forEach(([key, value]) => {
          el.setAttribute(key, value);
        });
        head.appendChild(el);
      });
    }

    // Additional Link Tags
    if (additionalLinkTags?.length) {
      additionalLinkTags.forEach(tag => {
        const el = document.createElement('link');
        Object.entries(tag).forEach(([key, value]) => {
          el.setAttribute(key, value);
        });
        head.appendChild(el);
      });
    }

    return () => {
      const selectorsToRemove = [
        'meta[name="description"]',
        'meta[name="keywords"]',
        'meta[name="language"]',
        'meta[name="author"]',
        'meta[name="robots"]',
        'meta[property="og:title"]',
        'meta[property="og:description"]',
        'meta[property="og:url"]',
        'meta[property="og:image"]',
        'meta[property="og:image:alt"]',
        'meta[property="og:image:width"]',
        'meta[property="og:image:height"]',
        'meta[property="og:image:type"]',
        'meta[property="og:type"]',
        'meta[property="og:site_name"]',
        'meta[property="og:locale"]',
        'meta[property="profile:first_name"]',
        'meta[property="profile:last_name"]',
        'meta[property="profile:username"]',
        'meta[property="profile:gender"]',
        'meta[property="article:published_time"]',
        'meta[property="article:modified_time"]',
        'meta[property="article:expiration_time"]',
        'meta[property="article:author"]',
        'meta[property="article:section"]',
        'meta[property="article:tag"]',
        'meta[name="twitter:card"]',
        'meta[name="twitter:site"]',
        'meta[name="twitter:title"]',
        'meta[name="twitter:description"]',
        'meta[name="twitter:image"]',
        'meta[name="twitter:image:alt"]',
        'meta[name="geo.region"]',
        'meta[name="geo.position"]',
        'meta[name="geo.placename"]',
        'meta[name="ICBM"]',
        'link[rel="canonical"]',
        'link[rel="alternate"]',
      ];
      
      selectorsToRemove.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => el.remove());
      });
    };
  }, [
    pageTitle, metaDescription, fullUrl, ogImage, type, publishedTime, modifiedTime, author, 
    noIndex, noFollow, twitter, og, geo, language, authorMeta, additionalMetaTags, additionalLinkTags, languageAlternates
  ]);

  return null;
};

export default SEO;
